/**
 * T025/US5 (FR-015, §24). Structure follows `List.tsx`'s own fan-out precedent (Catalog identity +
 * a per-row Warehouse call). `GET /stock/exceptions` has no pagination and no `kind` filter
 * (verified against `InventoryController.GetStockExceptions`: only an optional `warehouseId`), so
 * `PagedTable` pages the whole set in memory.
 *
 * **Backend gap, disclosed rather than fabricated** (see `contracts/backend-gaps.md` GAP-6):
 * spec US5-AS3/FR-015 names three exception categories — missing conversion factor, negative
 * balance, stale reservation. `StockService.ExceptionsAsync` (verified in source) filters only
 * `QtyStockingUnit < 0 || FreeStock < 0`; there is no missing-conversion-factor or
 * stale-reservation row on this endpoint at all, and no bulk stock list exists to scan for one
 * client-side (only `GET /stock/{skuId}` per id). The alert below states this rather than
 * pretending the list is complete.
 *
 * Only the data and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { Link, useLoaderData, useOutletContext } from "react-router-dom";
import { PiArrowsDownUpDuotone } from "react-icons/pi";
import { Alert, Button } from "rizzui";
import { EmptyState, ErrorSurface } from "@steelyard/theme";
import { PagedTable } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { TableSearch } from "../../components/table/toolbar";
import { can } from "../../lib/permissions";
import type { AccountSummary, PermissionState } from "../../types";
import { getStock, getStockExceptions } from "../../lib/services/warehouse";
import { exceptionsColumns, type ExceptionRow } from "./exceptions-columns";

export async function exceptionsLoader() {
  const exceptions = await getStockExceptions();
  // `StockExceptionRow` carries the SKU's code and name but no stocking-unit code — that one is
  // fanned out per row, over what is normally a short, in-trouble list. A label the API could not
  // resolve reads "—", never the id.
  const stocks = await Promise.all(exceptions.rows.map((row) => getStock(row.skuId)));
  const rows: ExceptionRow[] = exceptions.rows.map((row, index) => ({
    ...row,
    code: row.skuCode ?? "—",
    name: row.skuName ?? "—",
    unitCode: stocks[index]!.stockingUnitCode,
  }));
  return { reportingPeriodDays: exceptions.reportingPeriodDays, rows };
}

export default function InventoryExceptionsPage() {
  const { reportingPeriodDays, rows } = useLoaderData() as Awaited<ReturnType<typeof exceptionsLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();

  if (!can(permissionState, "inventory.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view stock exceptions" />;
  }

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Stock Exceptions"
        count={rows.length}
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Stock", href: "/inventory" }, { name: "Exceptions" }]}
        description={`SKUs whose balance needs looking at — the exceptions surface, not the full stock list; the portal is the authority for every figure here. A negative balance is flagged as needing attention once it has stood for more than ${reportingPeriodDays} day${Number(reportingPeriodDays) === 1 ? "" : "s"} (the configured reporting period).`}
      />

      <Alert color="info" bar>
        This list covers negative on-hand and negative free stock only. Missing conversion factors
        and stale reservations aren&apos;t reported here yet.
      </Alert>

      <PagedTable<ExceptionRow>
        actions={
          <Link to="/reports/stock-movement">
            <Button as="span" variant="outline">
              <PiArrowsDownUpDuotone aria-hidden className="me-1.5 size-4" />
              Movement report
            </Button>
          </Link>
        }
        result={{ items: rows, page: 1, pageSize: rows.length, totalCount: rows.length }}
        caption="Stock exceptions: SKU, on hand, free, and how long the balance has been negative"
        columns={exceptionsColumns}
        getRowId={(row) => row.skuId}
        search={(table) => <TableSearch table={table} placeholder="Search exceptions…" />}
        empty={<EmptyState title="No exceptions">Every SKU&apos;s balance is non-negative right now.</EmptyState>}
      />
    </div>
  );
}
